import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, Sparkles, Copy, Loader2, RefreshCw, ChevronDown } from "lucide-react";

export default function Inquiries() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [key]: { subject, body, busy } }

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/inquiries");
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load inquiries"); }
  }, []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const draftReply = async (it) => {
    const key = `${it._source}:${it._id_field}`;
    setDrafts(d => ({...d, [key]: {...(d[key]||{}), busy: true}}));
    try {
      const r = await api.post("/admin/inquiries/draft-reply", { source: it._source, id: it._id_field });
      setDrafts(d => ({...d, [key]: { subject: r.data.subject, body: r.data.body, busy: false, cost: r.data.usage?.cost_usd }}));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
      setDrafts(d => ({...d, [key]: {...(d[key]||{}), busy: false}}));
    }
  };

  const copyMailto = (it, key) => {
    const d = drafts[key] || {};
    const to = it.email || "";
    const subj = encodeURIComponent(d.subject || "");
    const body = encodeURIComponent(d.body || "");
    window.location.href = `mailto:${to}?subject=${subj}&body=${body}`;
  };

  const copyBody = (key) => {
    const b = drafts[key]?.body || "";
    navigator.clipboard.writeText(b).then(() => toast.success("Copied to clipboard"));
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="inquiries-page">
      <div className="flex items-center gap-3 mb-2">
        <Mail size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-4xl text-[color:var(--hb-navy)]">Inquiries</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Customer questions, contact forms, and service quotes. Click "Draft Reply" and the assistant will write you a personalized response in your voice.</p>

      <div className="flex justify-end mb-4">
        <button onClick={load} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5 flex items-center gap-1" data-testid="refresh"><RefreshCw size={12}/> Refresh</button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-neutral-300 text-sm text-neutral-500">No inquiries yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const key = `${it._source}:${it._id_field}`;
            const isOpen = expanded === key;
            const d = drafts[key];
            return (
              <div key={key} className="border border-neutral-200 bg-white" data-testid={`inq-${key}`}>
                <button onClick={() => setExpanded(isOpen ? null : key)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-neutral-50">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${it._source === "quote" ? "border-amber-400 text-amber-800 bg-amber-50" : "border-blue-400 text-blue-800 bg-blue-50"}`}>{it._source}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.name} <span className="text-neutral-500 font-normal">· {it.email}</span></div>
                    <div className="text-xs text-neutral-500 truncate">{(it.message || it.service_needed || "").slice(0, 100)}</div>
                  </div>
                  <div className="text-[10px] text-neutral-500 whitespace-nowrap">{it.created_at ? new Date(it.created_at).toLocaleDateString() : ""}</div>
                  <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                </button>

                {isOpen && (
                  <div className="border-t border-neutral-100 p-5 space-y-3" data-testid={`inq-${key}-details`}>
                    <div className="text-xs text-neutral-700 whitespace-pre-wrap">{it.message || it.service_needed}</div>
                    {it._source === "quote" && (
                      <div className="text-[11px] text-neutral-500 grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>Location: {it.location || "—"}</div>
                        <div>Preferred date: {it.preferred_date || "—"}</div>
                        <div>Phone: {it.phone || "—"}</div>
                        <div>Service type: {it.service_type || "—"}</div>
                      </div>
                    )}

                    {!d?.body && (
                      <button onClick={() => draftReply(it)} disabled={d?.busy} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-4 py-2 flex items-center gap-1.5 disabled:opacity-50" data-testid={`draft-btn-${key}`}>
                        {d?.busy ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Draft AI Reply
                      </button>
                    )}

                    {d?.body && (
                      <div className="border border-neutral-300 bg-[color:var(--hb-warm)] p-4 space-y-2" data-testid={`draft-${key}`}>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">AI Draft {d.cost && `· $${d.cost.toFixed(4)}`}</div>
                        <input value={d.subject} onChange={e => setDrafts(x => ({...x, [key]: {...x[key], subject: e.target.value}}))} className="w-full text-sm font-medium bg-white border border-neutral-300 px-2 py-1.5"/>
                        <textarea value={d.body} onChange={e => setDrafts(x => ({...x, [key]: {...x[key], body: e.target.value}}))} rows={10} className="w-full text-xs bg-white border border-neutral-300 px-2 py-1.5 whitespace-pre-wrap font-sans"/>
                        <div className="flex flex-wrap gap-2">
                          <button onClick={() => copyMailto(it, key)} className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-3 py-1.5 flex items-center gap-1" data-testid={`send-${key}`}>
                            <Mail size={11}/> Open in Email
                          </button>
                          <button onClick={() => copyBody(key)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5 flex items-center gap-1" data-testid={`copy-${key}`}>
                            <Copy size={11}/> Copy body
                          </button>
                          <button onClick={() => draftReply(it)} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1.5 flex items-center gap-1" data-testid={`redraft-${key}`}>
                            <Sparkles size={11}/> Regenerate
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8"><Link to="/admin" className="text-xs text-[color:var(--hb-royal)] uppercase tracking-widest">← Admin home</Link></div>
    </div>
  );
}
